import React, { useState } from 'react';
import { useShop } from '../context/ShopContext'; 
import { useAuth } from '../context/AuthContext'; 
import { Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CartPage: React.FC = () => {
  const { cartItems, removeItemFromCart, updateItemQuantity } = useShop(); 
  const { user } = useAuth(); 
  const navigate = useNavigate();
  
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const totalAmount = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);

  const handleCheckout = () => {
    if (!user) {
      setToastMessage("Please log in to your account to proceed with the checkout.");
      setTimeout(() => setToastMessage(null), 3500); 
    } else {
      navigate('/checkout'); 
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 min-h-screen relative">
      
      {toastMessage && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-fade-in-down">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            <span className="font-medium">{toastMessage}</span>
        </div>
      )}

      <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Your Shopping Cart</h1>

      {cartItems.length === 0 ? (
        <div className="text-center p-10 ">
            <p className="text-lg text-gray-600 mb-4">Your cart is currently empty.</p>
            <button onClick={() => navigate('/')} className="bg-amber-700 text-white py-2 px-6 rounded-md hover:bg-amber-800">Continue Shopping</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
             {cartItems.map((item) => (
              <div key={item.cartId} className="flex items-center p-4 border rounded-lg bg-white shadow-sm">
                <img src={item.productImage} alt={item.productTitle} className="w-20 h-20 object-cover rounded-md" />
                <div className="flex-grow ml-4">
                  <h3 className="text-lg font-semibold text-gray-800">{item.productTitle}</h3>
                  <p className="text-gray-600">Price: ₹{item.price.toFixed(2)}</p>
                </div>
                <div className="flex items-center space-x-2 mr-4 min-w-[120px]">
                  <button onClick={() => updateItemQuantity(item.cartId!, item.quantity - 1)} disabled={item.quantity <= 1} className="p-1 border rounded bg-gray-100 hover:bg-gray-200 transition disabled:opacity-50">-</button>
                  <input type="number" min="1" value={item.quantity} readOnly className="w-12 text-center border rounded p-1" />
                  <button onClick={() => updateItemQuantity(item.cartId!, item.quantity + 1)} className="p-1 border rounded bg-gray-100 hover:bg-gray-200 transition">+</button>
                </div>
                <div className="ml-auto flex items-center space-x-4">
                  <span className="font-bold text-lg min-w-[80px] text-right text-amber-800">₹{(item.price * item.quantity).toFixed(2)}</span>
                  <button onClick={() => removeItemFromCart(item.cartId!)} className="p-2 text-red-500 hover:text-red-700 transition"><Trash2 size={20} /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1 p-6 border rounded-lg bg-white shadow-lg h-fit sticky top-28">
            <h2 className="text-xl font-bold mb-4 border-b pb-2 text-gray-800">Order Summary</h2>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Items ({cartItems.length})</span>
              <span className="font-medium">₹{totalAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-4 text-xl font-bold text-amber-800">
              <span>Total Amount</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
            <button
              onClick={handleCheckout} 
              className="w-full mt-6 bg-amber-700 text-white py-3 rounded-md hover:bg-amber-800 transition duration-300 font-semibold shadow-md"
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;